/**
 * How tall a worksheet is, in the pixels the browser will actually use.
 *
 * A page of children's work that runs three lines onto a second sheet is
 * worse than useless — the teacher photocopies twenty-five of them and only
 * finds out at the guillotine. Browsers give no way to ask "will this fit"
 * before printing, so the arithmetic is done here instead, from the same
 * numbers the renderer draws with. Change a rule's height in one place and
 * both the page and the check that guards it move together.
 *
 * CSS pixels, at the 96dpi a browser assumes for print. Letter paper is
 * 8.5 × 11 inches, so 816 × 1056, and half an inch of margin on every side
 * leaves 720 × 960 to work in.
 */
export const PAGE = {
    width: 816,
    height: 1056,
    /** Half an inch. Anything less and a home printer clips the edges. */
    margin: 48,
};
export const CONTENT_WIDTH = PAGE.width - PAGE.margin * 2;
export const CONTENT_HEIGHT = PAGE.height - PAGE.margin * 2;
/**
 * One line of handwriting rules, and the space under it.
 *
 * 36px is about 3/8 of an inch between the solid lines, which is the size
 * first-grade paper actually comes in — 30 was a guess and it made the letters
 * a six-year-old is meant to trace too small to trace.
 */
export const RULE_HEIGHT = 36;
export const RULE_GAP = 14;
/** Between one part and the next. */
export const PART_GAP = 16;
/** A working-out box on its own: room for a fist and a fat pencil. */
export const SINGLE_BOX = 120;
/* The first-grade-friendly parts, sized for six-year-old hands. */
/** A matching row: far enough apart that the drawn lines stay legible. */
export const MATCH_ROW = 44;
/** A row of options big enough to circle with a fat pencil. */
export const CIRCLE_ROW = 40;
/** A glue box: bigger than the thing being glued into it. */
export const GLUE_BOX = 92;
/** The strip of things to cut out, along the bottom of the page. */
export const CUTOUT_STRIP = 76;
/*
 * Title, name line and the rule under them.
 *
 * The title is text-2xl — 24px at 1.25 line height, so 30 — plus 8px of
 * padding and a 2px rule. The first version of this called it 34 and every
 * worksheet came out 20px taller than the arithmetic said.
 */
const HEADER_HEIGHT = 30 + 10 + 2;
/** Roughly 85 characters to a line at the instruction size, at 20px each. */
const INSTRUCTION_LINE = 20;
const FOOTER_HEIGHT = 12 + 16;
function rules(count) {
    return count * RULE_HEIGHT + Math.max(0, count - 1) * RULE_GAP;
}
/** Lines of wrapped text, at a given characters-per-line. */
function wrapped(text, perLine) {
    return Math.max(1, Math.ceil(text.length / perLine));
}
/**
 * The order the parts are actually printed in.
 *
 * A cut-out strip goes last, always, whatever order it was written in. The
 * child cuts along the dashed line, and anything below that line leaves the
 * page with the cut-outs — so a question printed underneath one is a question
 * that ends up in the bin. Ordering it here rather than trusting whoever
 * built the page means the rule holds for a worksheet the teacher assembles
 * herself, in whatever order she happens to add things.
 */
export function orderedParts(parts) {
    const above = parts.filter((p) => p.kind !== 'cutandpaste');
    const below = parts.filter((p) => p.kind === 'cutandpaste');
    return [...above, ...below];
}
/**
 * More than one page, when one is genuinely not enough.
 *
 * Four is not a limit anybody will reach; it is there so a stuck key cannot
 * ask a classroom printer for two hundred sheets.
 */
export const MAX_PAGES = 4;
/**
 * The parts, split into the sheets of paper they print on.
 *
 * A `pagebreak` is where the teacher said one page ends and the next begins,
 * so nothing has to be guessed at: each page is measured on its own and either
 * fits or is refused while she is still editing. The cut-out rule is applied
 * *within* each page — a strip belongs at the bottom of its own sheet, not at
 * the end of the document.
 */
export function splitPages(parts) {
    const pages = [[]];
    for (const part of parts) {
        if (part.kind === 'pagebreak') {
            if (pages.length < MAX_PAGES)
                pages.push([]);
            continue;
        }
        pages[pages.length - 1].push(part);
    }
    return pages.map(orderedParts);
}
/**
 * The pages that actually reach the printer.
 *
 * A page with nothing on it is dropped. While she is editing, a page she has
 * just added and not filled yet is a legitimate thing to be looking at; sent
 * to a printer it is a blank sheet in a stack of twenty-five, which is the
 * single thing this whole file exists to prevent.
 */
export function printedPages(parts) {
    const filled = splitPages(parts).filter((page) => page.length > 0);
    return filled.length > 0 ? filled : [[]];
}
/** Pages back to one list, with the breaks put back between them. */
export function flattenPages(pages) {
    return pages.flatMap((page, i) => i === 0 ? page : [{ kind: 'pagebreak' }, ...page]);
}
export function partHeight(part) {
    switch (part.kind) {
        case 'text':
            return wrapped(part.text, 85) * 20;
        case 'handwriting':
        case 'lines':
            return rules(part.lines);
        case 'boxes': {
            const rows = Math.ceil(part.count / part.columns);
            const box = part.count === 1 ? SINGLE_BOX : 30;
            return (part.label ? 22 : 0) + rows * box + (rows - 1) * 4;
        }
        case 'problems': {
            const rows = part.problems.reduce((total, problem) => {
                if (part.answerLines === 0) {
                    // The sum and its 32px answer box share one line.
                    return total + 32;
                }
                return total + wrapped(problem, 75) * 22 + 8 + rules(part.answerLines);
            }, 0);
            return rows + Math.max(0, part.problems.length - 1) * 10;
        }
        case 'wordbank':
            // A centred, wrapped row of words inside its dashed box.
            return 20 + wrapped(part.words.join('   '), 70) * 20 + 16;
        case 'drawbox':
            return part.height + 20;
        case 'numberline':
            return 50;
        case 'tenframe': {
            // Four frames to a row at 8.5 inches wide.
            const rows = Math.ceil(part.frames / 4);
            return rows * 64 + (rows - 1) * 16;
        }
        case 'checklist':
            return part.items.length * 24 + (part.items.length - 1) * 4;
        case 'table':
            // A 30px header row and 40px of writing room per row.
            return 30 + part.rows * 40;
        case 'matching': {
            // A row per pair, tall enough for a line to be drawn without crossing
            // its neighbours.
            const pairs = Math.max(part.left.length, part.right.length);
            return pairs * MATCH_ROW;
        }
        case 'circleit':
            return part.questions.reduce((total, q) => total + wrapped(q.prompt, 70) * 20 + CIRCLE_ROW + 8, 0);
        case 'sort':
            return 26 + part.height;
        case 'cutandpaste': {
            // Zero glue boxes is normal: the page may have a sorting grid above the
            // cut line for the pieces to go into. Max, or the gap arithmetic goes
            // negative and the page measures shorter than it prints.
            const glueRows = Math.ceil(part.glueBoxes / 3);
            const gaps = Math.max(0, glueRows - 1) * 8;
            // The strip along the bottom, and the scissors rule above it.
            return (part.glueLabel ? 22 : 0) + glueRows * GLUE_BOX + gaps + 24 + CUTOUT_STRIP;
        }
        // A divider, not a thing on the paper.
        case 'pagebreak':
            return 0;
    }
}
/**
 * What one page comes to, given where it sits in the document.
 *
 * The title and name line are on every sheet — pages get separated, and a page
 * of a six-year-old's work with no name on it belongs to nobody. The spoken
 * instructions are only on the first, and the standards footnote only on the
 * last, which is why this needs to know which page it is measuring.
 */
export function pageHeight(sheet, parts, { first, last }) {
    const instructions = first && sheet.instructions ? 8 + wrapped(sheet.instructions, 95) * INSTRUCTION_LINE : 0;
    const body = parts.reduce((total, part) => total + partHeight(part), 0);
    const gaps = Math.max(0, parts.length - 1) * PART_GAP;
    const footer = last && sheet.standards.length > 0 ? FOOTER_HEIGHT : 0;
    return HEADER_HEIGHT + instructions + 16 + body + gaps + footer;
}
/** Every page's height, in order. */
export function pageHeights(sheet) {
    const pages = splitPages(sheet.parts);
    return pages.map((parts, i) => pageHeight(sheet, parts, { first: i === 0, last: i === pages.length - 1 }));
}
/** How tall the whole worksheet is. Only meaningful for a one-page sheet. */
export function worksheetHeight(sheet) {
    return pageHeights(sheet).reduce((total, height) => total + height, 0);
}
/** How many sheets of paper come out of the printer. */
export function pagesNeeded(sheet) {
    return printedPages(sheet.parts).length;
}
/**
 * Which pages are over the bottom of the paper, and by how much.
 *
 * Empty means it prints as written. Anything in here is a page whose last few
 * lines the printer would push onto a sheet of its own — twenty-five sheets of
 * nine-tenths white paper, discovered at the guillotine.
 */
export function overfullPages(sheet) {
    return pageHeights(sheet)
        .map((height, i) => ({ page: i + 1, over: Math.round(height - CONTENT_HEIGHT) }))
        .filter((p) => p.over > 0);
}
//# sourceMappingURL=worksheetLayout.js.map